import React, { useEffect, useMemo, useRef, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Pill from "../../../components/v3/Pill";
import Btn from "../../../components/v3/Btn";
import Field from "../../../components/v3/Field";
import { bdcApi } from "../../../services/bdcApi";
import { usePermissions } from "../../../context/permissionContext";
import { validatePermission } from "../../../utils/permissionUtils";
import { useRole } from "../../../hooks/useRole";

/**
 * CampaignComposer: newsletter campaigns (consultant-import WI-4; marketing staff 2026-10).
 *
 * Compose a DRAFT, open it, edit it (every save is a new revision), preview it, send a test of THIS revision
 * to your own address, count the audience, type the count back, and send. The API enforces every step
 * (gates "Marketing Campaigns:View" / ":Edit" / ":Send"; the send refuses an untested revision or a count
 * that changed); this screen mirrors the rules so the reason is shown before the request.
 *
 * The preview renders the stored body in a sandboxed iframe (no scripts, no same-origin): staff-authored
 * HTML never runs in the admin's session. Delivery status is derived from email_logs, so it always agrees
 * with the Email log viewer. A 503 means the kill switch (feature.marketing_email) is off.
 *
 * Keeping the screen honest about the STORED draft (Codex admin reviews). The API refuses every stale send,
 * so this is about what the screen shows, but it must never show an old version as tested and ready:
 *   - an editor generation: every response for the open draft is applied only if the editor still shows that
 *     draft (Close, or opening another, makes older responses obsolete); Close is always available;
 *   - after ANY non-success of save / test / send (a refusal, a 429, a network error, a send whose answer was
 *     lost), and after a test that covered a different revision, the stored draft is read again; the
 *     original error stays on screen unless the stored draft actually moved;
 *   - unsaved edits survive a reload; if the draft was sent meanwhile they are shown read-only to copy;
 *   - fields are read-only while a request is in flight (nothing typed then is overwritten).
 */

const AUDIENCE_LABELS = {
  SUBSCRIBERS: "Newsletter subscribers (double-opted-in)",
  OPTED_IN_USERS: "Registered users opted into the newsletter",
  ALL: "Both (each address once)",
};

const STATUS_TONE = { DRAFT: "amber", ENQUEUED: "mint", CANCELLED: "gray" };

// The API's refusal codes, in words.
const MESSAGES = {
  test_send_required: "Send a test of this version to yourself first. Any edit after a test needs a new test.",
  campaign_changed: "This draft was changed since you opened it. Reload it and check the latest version.",
  campaign_not_in_draft: "This campaign has already been sent.",
  test_send_too_soon: "Please wait 30 seconds between test sends.",
  campaign_busy: "Another action on this campaign is still running. Try again in a moment.",
  body_html_too_large: "The body is larger than 200 KB. Remove large inline content and try again.",
  body_html_required: "The body is empty (or contained only content that is not allowed in an email).",
  subject_required: "A subject is required.",
  invalid_audience: "Choose an audience.",
  no_email_on_account: "Your account has no email address to send the test to.",
  confirm_audience_count_required: "Type the number of recipients to confirm the send.",
  content_revision_required: "Reload the campaign and try again.",
  feature_disabled: "Marketing email is switched off (feature.marketing_email).",
  permission_denied: "You do not have permission for this action.",
};

export function messageFor(err, fallback) {
  const data = err?.response?.data || {};
  if (data.message === "audience_count_changed" && data.data) {
    return `The audience changed to ${data.data.current} recipient(s) since it was counted. Count again and type the new number.`;
  }
  if (err?.response?.status === 503) return MESSAGES.feature_disabled;
  return MESSAGES[data.message] || MESSAGES[data.code] || data.message || err?.message || fallback;
}

// The stored body inside a minimal document, for the sandboxed preview.
export const previewDocument = (subject, bodyHtml) =>
  `<!doctype html><html><head><meta charset="utf-8"><title>${String(subject || "")
    .replace(/[<>&"]/g, "")}</title></head><body>${bodyHtml || ""}</body></html>`;

function CampaignComposer() {
  const { permissions, permissionsMapping } = usePermissions();
  const { hasRole } = useRole();
  const isAD = hasRole("AD");
  const can = (path) => isAD || validatePermission(path, permissions || [], permissionsMapping || {});
  const canEdit = can("Marketing Campaigns:Edit");
  const canSend = can("Marketing Campaigns:Send");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({ subject: "", body_html: "", audience: "ALL" });

  // The draft open in the editor: the stored row plus the local edits.
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const [count, setCount] = useState(null);
  const [typedCount, setTypedCount] = useState("");
  // The screen's copy of the draft is uncertain (an action did not succeed): Send stays off until the stored
  // row has been read again.
  const [stale, setStale] = useState(false);
  // Unsaved text of a draft that stopped being a draft meanwhile (sent by someone else): shown to copy.
  const [retained, setRetained] = useState(null);
  // Bumped whenever the editor stops showing a draft (Close, Open): older responses are then ignored.
  const editorGen = useRef(0);

  async function refresh() {
    setLoading(true);
    try {
      const res = await bdcApi.adListCampaigns({ limit: 25 });
      setCampaigns(res?.data?.rows || []);
      setError(null);
    } catch (err) {
      setError(messageFor(err, "Failed to load campaigns"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  // Runs one request with the screen busy. A response that lands after the editor moved on (Close / Open) is
  // ignored: no flash, no follow-up, and it does not release a newer action's busy state.
  async function run(action, fallback, onError) {
    const g = editorGen.current;
    setBusy(true);
    setFlash(null);
    try {
      return await action();
    } catch (err) {
      if (g === editorGen.current) {
        setFlash({ tone: "danger", text: messageFor(err, fallback) });
        if (onError) await onError(err);
      }
      return null;
    } finally {
      if (g === editorGen.current) setBusy(false);
    }
  }

  function closeEditor() {
    editorGen.current += 1;
    setOpen(null);
    setEdit(null);
    setStale(false);
    setRetained(null);
    setCount(null);
    setTypedCount("");
    setBusy(false);
  }

  async function createDraft(e) {
    e.preventDefault();
    const submitted = form;
    const res = await run(() => bdcApi.adCreateCampaign(submitted), "Save failed");
    if (!res) return;
    setFlash({ tone: "info", text: "Draft saved. Open it to preview, test and send." });
    // Only if the form still holds what was submitted: Close releases the screen while a create is in flight,
    // and anything typed since must survive the late answer.
    setForm((cur) => (cur.subject === submitted.subject && cur.body_html === submitted.body_html && cur.audience === submitted.audience
      ? { subject: "", body_html: "", audience: "ALL" }
      : cur));
    await refresh();
  }

  async function openCampaign(id) {
    editorGen.current += 1;
    const g = editorGen.current;
    const res = await run(() => bdcApi.adGetCampaign(id), "Could not open the campaign");
    if (!res || g !== editorGen.current) return;
    const row = res.data;
    setRetained(null);
    setOpen(row);
    setEdit({ subject: row.subject, audience: row.audience, body_html: row.body_html });
    setCount(null);
    setTypedCount("");
    setStale(false);
  }

  const dirty = useMemo(() => !!(open && edit && (
    edit.subject !== open.subject || edit.audience !== open.audience || edit.body_html !== open.body_html
  )), [open, edit]);
  const tested = !!(open && open.test_revision && open.test_revision === open.content_revision);

  /*
   * Read the stored draft again (see the header). `shown` is the row this screen displayed when the action
   * began, `edits` the unsaved edits then (null when there were none). Applied only if the editor still shows
   * that draft. Says what moved; when nothing did, the original message stays.
   */
  async function reconcile(g, shown, edits, { quiet = false } = {}) {
    setStale(true);
    setCount(null);
    setTypedCount("");
    try {
      const res = await bdcApi.adGetCampaign(shown.id);
      if (g !== editorGen.current) return null;
      const row = res.data;
      const moved = row.content_revision !== shown.content_revision || row.status !== shown.status
        || row.test_revision !== shown.test_revision;
      setOpen(row);
      setStale(false);
      if (!edits) setEdit({ subject: row.subject, audience: row.audience, body_html: row.body_html });
      if (row.status !== "DRAFT") {
        if (edits) setRetained(edits);
        if (!quiet) {
          setFlash({ tone: "warn", text: `This campaign is no longer a draft (${row.status}).${edits ? " Your unsaved text is shown below so you can copy it into a new draft." : ""}` });
        }
      } else if (moved && !quiet) {
        setFlash({ tone: "warn", text: `This draft changed since you opened it. Now showing version ${row.content_revision}${edits ? "; your unsaved edits are kept, and saving them replaces that version" : ""}.` });
      }
      return row;
    } catch (_) {
      return null; // stays stale; the Reload button retries
    }
  }
  const afterFailure = (g, shown, edits) => () => reconcile(g, shown, edits);

  // The Reload button: busy, but the message on screen stays unless the re-read reports a change.
  async function reloadNow() {
    const g = editorGen.current;
    setBusy(true);
    try {
      const row = await reconcile(g, open, dirty ? edit : null);
      if (!row && g === editorGen.current) {
        setFlash({ tone: "danger", text: "The draft could not be read. Check your connection and press Reload again." });
      }
    } finally {
      if (g === editorGen.current) setBusy(false);
    }
  }

  async function saveDraft() {
    const g = editorGen.current;
    const shown = open;
    const edits = edit;
    const res = await run(() => bdcApi.adUpdateCampaign(shown.id, { content_revision: shown.content_revision, ...edits }), "Save failed", afterFailure(g, shown, edits));
    if (!res || g !== editorGen.current) return;
    setOpen(res.data);
    setEdit({ subject: res.data.subject, audience: res.data.audience, body_html: res.data.body_html });
    setCount(null);
    setTypedCount("");
    setFlash({ tone: "info", text: `Saved as version ${res.data.content_revision}. Send a test of this version before sending.` });
    await refresh();
  }

  async function sendTest() {
    const g = editorGen.current;
    const shown = open;
    const res = await run(() => bdcApi.adTestSendCampaign(shown.id), "The test could not be sent", afterFailure(g, shown, null));
    if (!res || g !== editorGen.current) return;
    const tested = res.data.test_revision;
    const note = `Test of version ${tested} queued to ${res.data.sent_to}. Check your inbox before sending.`;
    if (tested !== shown.content_revision) {
      // Someone saved a newer version before the test ran: the API tested THAT version, so show it.
      setBusy(true);
      const row = await reconcile(g, shown, null, { quiet: true });
      if (g !== editorGen.current) return;
      setBusy(false);
      setFlash({ tone: "warn", text: row
        ? `${note} The draft had changed since you opened it: now showing version ${row.content_revision}.`
        : `${note} The draft had changed since you opened it; reload it before sending.` });
      return;
    }
    setOpen({ ...shown, test_revision: tested, test_sent_at: new Date().toISOString() });
    setFlash({ tone: "info", text: note });
  }

  async function countRecipients() {
    const g = editorGen.current;
    const res = await run(() => bdcApi.adCampaignPreviewCount(open.id), "Could not count the audience");
    if (!res || g !== editorGen.current) return;
    setCount(res.data);
    setTypedCount("");
  }

  async function sendCampaign() {
    if (!count || String(count.recipients) !== typedCount.trim()) return;
    // Outward-facing and irreversible.
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Send "${open.subject}" to ${count.recipients} recipient(s)? This cannot be undone.`)) return;
    const g = editorGen.current;
    const shown = open;
    const res = await run(
      () => bdcApi.adSendCampaign(shown.id, { content_revision: shown.content_revision, confirm_audience_count: count.recipients }),
      "Send failed",
      afterFailure(g, shown, null)
    );
    if (!res) return;
    if (g !== editorGen.current) {
      // the editor was closed meanwhile; the list still has to show the send
      await refresh();
      return;
    }
    setFlash({ tone: "info", text: `Campaign queued to ${res.data.enqueued_count} recipient(s). The mail queue sends at the marketing rate cap.` });
    closeEditor();
    await refresh();
    await checkStatus(res.data.id);
  }

  async function checkStatus(id) {
    const res = await run(() => bdcApi.adCampaignStatus(id), "Status fetch failed");
    if (res) setStatuses((s) => ({ ...s, [id]: res.data }));
  }

  const isDraft = open && open.status === "DRAFT";
  const sendReady = isDraft && tested && !dirty && !stale && count && String(count.recipients) === typedCount.trim();

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Marketing campaigns" />

      {error && <Alert tone="danger">{error}</Alert>}
      {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}

      {canEdit && (
        <section className="card">
          <div className="card__head">
            <div className="card__title">New campaign</div>
          </div>
          <div className="card__body">
            <form onSubmit={createDraft} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="Subject *" name="campaign-subject" required maxLength={255} disabled={busy}
                value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              <Field as="select" label="Audience" name="campaign-audience" disabled={busy}
                value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                {Object.entries(AUDIENCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Field>
              <Field as="textarea" label="Body (HTML) *" name="campaign-body" rows={10} required disabled={busy}
                value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                hint="Paste the newsletter HTML. The branded header and the footer with the unsubscribe link are added when it is sent. Scripts, forms and embedded frames are removed." />
              <Btn variant="primary" type="submit" disabled={busy}>{busy ? "Saving..." : "Save draft"}</Btn>
            </form>
          </div>
        </section>
      )}

      {open && edit && (
        <section className="card" data-testid="campaign-editor">
          <div className="card__head">
            <div className="card__title">
              {isDraft ? "Draft" : "Campaign"}: {open.subject}{" "}
              <Pill tone={STATUS_TONE[open.status] || "gray"}>{open.status}</Pill>
            </div>
            <Btn size="sm" type="button" onClick={closeEditor}>Close</Btn>
          </div>
          <div className="card__body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="muted" data-testid="campaign-version">
              Version {open.content_revision}.{" "}
              {tested
                ? `Tested at this version${open.test_sent_at ? ` (${new Date(open.test_sent_at).toLocaleString()})` : ""}.`
                : "Not tested at this version yet."}
            </div>
            {stale && !busy && (
              <Alert tone="warn">
                This draft could not be read again, so the version shown may be out of date.{" "}
                <Btn size="sm" type="button" onClick={reloadNow}>Reload</Btn>
              </Alert>
            )}
            {retained && (
              <div data-testid="campaign-retained" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Field label="Your unsaved subject (copy it)" name="retained-subject" value={retained.subject} readOnly />
                <Field label="Your unsaved audience" name="retained-audience" value={AUDIENCE_LABELS[retained.audience] || retained.audience} readOnly />
                <Field as="textarea" label="Your unsaved body (copy it)" name="retained-body" rows={8} value={retained.body_html} readOnly />
              </div>
            )}

            {isDraft && canEdit && (
              <>
                <Field label="Subject" name="edit-subject" maxLength={255} value={edit.subject} disabled={busy}
                  onChange={(e) => setEdit({ ...edit, subject: e.target.value })} />
                <Field as="select" label="Audience" name="edit-audience" value={edit.audience} disabled={busy}
                  onChange={(e) => setEdit({ ...edit, audience: e.target.value })}>
                  {Object.entries(AUDIENCE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Field>
                <Field as="textarea" label="Body (HTML)" name="edit-body" rows={10} value={edit.body_html} disabled={busy}
                  onChange={(e) => setEdit({ ...edit, body_html: e.target.value })} />
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn type="button" variant="primary" disabled={busy || !dirty} onClick={saveDraft}>Save changes</Btn>
                  <Btn type="button" disabled={busy || dirty} onClick={sendTest}
                    title={dirty ? "Save your changes first" : "Sends one copy of this version to your own email address"}>
                    Send test to me
                  </Btn>
                </div>
              </>
            )}

            <div>
              <div className="field__label">Preview (as stored{isDraft && canEdit && dirty ? "; save to preview your edits" : ""})</div>
              <iframe
                title="Campaign preview"
                data-testid="campaign-preview"
                sandbox=""
                srcDoc={previewDocument(open.subject, open.body_html)}
                style={{ width: "100%", minHeight: 420, border: "1px solid var(--line, #ddd)", background: "#fff" }}
              />
            </div>

            {isDraft && canSend && (
              <div className="card" style={{ padding: 12 }} data-testid="campaign-send">
                <div className="card__title" style={{ marginBottom: 8 }}>Send to the audience</div>
                {!tested && <Alert tone="warn">Send a test of this version to yourself first.</Alert>}
                {dirty && <Alert tone="warn">Save your changes, then test the new version.</Alert>}
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <Btn type="button" disabled={busy} onClick={countRecipients}>Count recipients</Btn>
                  {count && (
                    <span className="muted" data-testid="campaign-count">
                      {count.recipients} recipient(s) ({count.suppressed} unsubscribed or opted out, left out)
                    </span>
                  )}
                </div>
                {count && (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8, flexWrap: "wrap" }}>
                    <Field label="Type the number of recipients to confirm" name="confirm-count" inputMode="numeric"
                      value={typedCount} onChange={(e) => setTypedCount(e.target.value)} />
                    <Btn type="button" variant="primary" disabled={busy || !sendReady} onClick={sendCampaign}>
                      Send to {count.recipients} recipient(s)
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="card">
        <div className="card__head">
          <div className="card__title">Campaigns</div>
        </div>
        <div className="card__body card__body--flush">
          {campaigns.length === 0 ? (
            <Empty>No campaigns yet.</Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Audience</th>
                  <th>Status</th>
                  <th>Version</th>
                  <th>Created</th>
                  <th>Recipients</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const stat = statuses[c.id];
                  return (
                    <React.Fragment key={c.id}>
                      <tr>
                        <td>{c.subject}</td>
                        <td className="muted">{c.audience}</td>
                        <td><Pill tone={STATUS_TONE[c.status] || "gray"}>{c.status}</Pill></td>
                        <td className="muted">
                          {c.content_revision}
                          {c.status === "DRAFT" && (c.test_revision === c.content_revision ? " (tested)" : " (not tested)")}
                        </td>
                        <td className="muted">{new Date(c.createdAt).toLocaleDateString()}</td>
                        <td className="muted">{c.status === "DRAFT" ? "Not sent" : c.enqueued_count}</td>
                        <td style={{ display: "flex", gap: 6 }}>
                          <Btn size="sm" type="button" disabled={busy} onClick={() => openCampaign(c.id)}>Open</Btn>
                          {c.status === "ENQUEUED" && (
                            <Btn size="sm" type="button" disabled={busy} onClick={() => checkStatus(c.id)}>Delivery status</Btn>
                          )}
                        </td>
                      </tr>
                      {stat && (
                        <tr>
                          <td colSpan={7} className="muted" style={{ fontSize: "var(--t-sm)" }}>
                            Delivery: queued {stat.delivery.queued}, sent {stat.delivery.sent}, failed {stat.delivery.failed},
                            {" "}not delivered {stat.delivery.dead_letter}, sending now {stat.delivery.processing}
                            {" "}(details in the Email log viewer)
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </PageShell>
  );
}

export default CampaignComposer;
